import { useState, useEffect, useMemo } from 'react';
import { Scale, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { workoutApi } from '@/services/api';
import { Card } from '@/components/common';
import type { Workout } from '@/types';

interface WeightDataPoint {
  date: Date;
  weight: number;
}

const TIME_RANGES = [
  { label: '7 nap', days: 7 },
  { label: '14 nap', days: 14 },
  { label: '30 nap', days: 30 },
];

export const WeightChart = () => {
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState(30);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    const loadWorkouts = async () => {
      try {
        // Load workouts with pagination to get enough data
        let allWorkouts: Workout[] = [];
        let cursor: string | undefined;
        let hasMore = true;

        while (hasMore && allWorkouts.length < 100) {
          const response = await workoutApi.getAll(cursor);
          if (response.success && response.data) {
            allWorkouts = [...allWorkouts, ...response.data.items];
            cursor = response.data.nextCursor || undefined;
            hasMore = !!response.data.nextCursor;
          } else {
            hasMore = false;
          }
        }

        setWorkouts(allWorkouts);
      } catch (error) {
        console.error('Failed to load workouts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadWorkouts();
  }, []);

  // Filter workouts with bodyWeight and within selected range
  const weightData = useMemo(() => {
    const now = new Date();
    const cutoffDate = new Date(now.getTime() - selectedRange * 24 * 60 * 60 * 1000);

    const data: WeightDataPoint[] = workouts
      .filter(w => w.bodyWeight && new Date(w.startTime) >= cutoffDate)
      .map(w => ({
        date: new Date(w.startTime),
        weight: w.bodyWeight!,
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    return data;
  }, [workouts, selectedRange]);

  // Calculate stats
  const stats = useMemo(() => {
    if (weightData.length === 0) return null;

    const weights = weightData.map(d => d.weight);
    const min = Math.min(...weights);
    const max = Math.max(...weights);
    const first = weights[0];
    const last = weights[weights.length - 1];
    const change = last - first;
    const avg = weights.reduce((a, b) => a + b, 0) / weights.length;

    return { min, max, first, last, change, avg };
  }, [weightData]);

  // Generate SVG path for the chart
  const chartPath = useMemo(() => {
    if (weightData.length < 2 || !stats) return null;

    const width = 100;
    const height = 50;
    const padding = 5;

    const minWeight = stats.min - 1;
    const maxWeight = stats.max + 1;
    const weightRange = maxWeight - minWeight;

    const points = weightData.map((d, i) => {
      const x = padding + (i / (weightData.length - 1)) * (width - 2 * padding);
      const y = height - padding - ((d.weight - minWeight) / weightRange) * (height - 2 * padding);
      return { x, y };
    });

    const pathD = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
      .join(' ');

    // Area fill path
    const areaD = `${pathD} L ${points[points.length - 1].x} ${height - padding} L ${points[0].x} ${height - padding} Z`;

    return { pathD, areaD, points };
  }, [weightData, stats]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Card title="Súlyváltozás">
        <div className="h-48 flex items-center justify-center">
          <div className="inline-block w-6 h-6 border-3 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Card>
    );
  }

  if (weightData.length === 0) {
    return (
      <Card title="Súlyváltozás">
        <div className="h-48 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
          <Scale className="w-12 h-12 mb-2 text-gray-300 dark:text-gray-600" />
          <p className="text-sm">Nincs súly adat az elmúlt {selectedRange} napban</p>
          <p className="text-xs mt-1">Add meg a testsúlyodat edzés indításakor</p>
        </div>
        {/* Time range selector */}
        <div className="mt-4 flex justify-center gap-2">
          {TIME_RANGES.map(range => (
            <button
              key={range.days}
              onClick={() => setSelectedRange(range.days)}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                selectedRange === range.days
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card 
      title="Súlyváltozás"
      headerActions={
        <div className="flex gap-1">
          {TIME_RANGES.map(range => (
            <button
              key={range.days}
              onClick={() => setSelectedRange(range.days)}
              className={`px-2 py-0.5 text-xs rounded transition-colors ${
                selectedRange === range.days
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      }
    >
      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">Kezdő</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{stats.first.toFixed(1)} kg</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">Jelenlegi</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{stats.last.toFixed(1)} kg</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">Változás</p>
            <div className={`flex items-center justify-center gap-1 text-lg font-semibold ${
              stats.change < 0 ? 'text-green-600 dark:text-green-400' : stats.change > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'
            }`}>
              {stats.change < 0 ? (
                <TrendingDown className="w-4 h-4" />
              ) : stats.change > 0 ? (
                <TrendingUp className="w-4 h-4" />
              ) : (
                <Minus className="w-4 h-4" />
              )}
              <span>{stats.change > 0 ? '+' : ''}{stats.change.toFixed(1)} kg</span>
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="relative w-full aspect-[2/1]">
        {chartPath && stats && (
          <svg
            viewBox="0 0 100 50"
            className="w-full h-full"
            preserveAspectRatio="none"
          >
            {/* Grid lines */}
            <line x1="5" y1="5" x2="95" y2="5" stroke="#e5e7eb" strokeWidth="0.3" />
            <line x1="5" y1="25" x2="95" y2="25" stroke="#e5e7eb" strokeWidth="0.3" />
            <line x1="5" y1="45" x2="95" y2="45" stroke="#e5e7eb" strokeWidth="0.3" />

            {/* Area fill */}
            <path
              d={chartPath.areaD}
              fill="url(#weightGradient)"
              opacity="0.3"
            />

            {/* Line */}
            <path
              d={chartPath.pathD}
              fill="none"
              stroke="#3b82f6"
              className="chart-line"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Points with hover */}
            {chartPath.points.map((p, i) => (
              <g key={i}>
                {/* Invisible larger hit area */}
                <circle
                  cx={p.x}
                  cy={p.y}
                  r="4"
                  fill="transparent"
                  className="cursor-pointer"
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                />
                {/* Visible point */}
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={hoveredIndex === i ? 2.5 : 1.5}
                  fill="#3b82f6"
                  className="transition-all duration-150 pointer-events-none"
                />
              </g>
            ))}

            {/* Gradient definition */}
            <defs>
              <linearGradient id="weightGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        )}

        {/* Y-axis labels */}
        {stats && (
          <>
            <span className="absolute top-0 left-0 text-xs text-gray-400">
              {stats.max.toFixed(0)}
            </span>
            <span className="absolute bottom-0 left-0 text-xs text-gray-400">
              {stats.min.toFixed(0)}
            </span>
          </>
        )}

        {/* Tooltip */}
        {hoveredIndex !== null && chartPath && (
          <div
            className="absolute bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg pointer-events-none z-10 whitespace-nowrap"
            style={{
              left: `${chartPath.points[hoveredIndex].x}%`,
              top: `${chartPath.points[hoveredIndex].y * 2}%`,
              transform: 'translate(-50%, -120%)',
            }}
          >
            <div className="font-semibold">{weightData[hoveredIndex].weight.toFixed(1)} kg</div>
            <div className="text-gray-300">{formatDate(weightData[hoveredIndex].date)}</div>
          </div>
        )}
      </div>

      {/* X-axis labels */}
      {weightData.length > 0 && (
        <div className="flex justify-between mt-1 text-xs text-gray-400">
          <span>{formatDate(weightData[0].date)}</span>
          <span>{formatDate(weightData[weightData.length - 1].date)}</span>
        </div>
      )}

      {/* Data points count */}
      <p className="text-xs text-gray-400 text-center mt-2">
        {weightData.length} mérés az elmúlt {selectedRange} napban
      </p>
    </Card>
  );
};
